import React, { useEffect, useMemo, useState } from 'react';
import { FiDownload, FiExternalLink, FiChevronDown, FiChevronRight, FiPlus, FiTrash2, FiFileText, FiTable } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AmortizationModal from './AmortizationModal';
import {
  DEAL_TYPES,
  PAYMENT_FREQUENCIES,
  packetsForDealType,
  computeDerived,
  DERIVED_LABELS,
} from '../../config/dealPacketForms';
import { downloadPacket, openPacket } from '../../services/dealFormsService';

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

const Field = ({ label, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

const TextInput = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <Field label={label}>
    <input
      type={type}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={inputCls}
    />
  </Field>
);

const MoneyInput = ({ label, value, onChange, placeholder }) => (
  <Field label={label}>
    <input
      type="number"
      step="0.01"
      value={value === 0 || value === '' || value == null ? (value === 0 ? 0 : '') : value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
      className={inputCls}
    />
  </Field>
);

const Section = ({ title, open, onToggle, children }) => (
  <div className="border rounded-lg">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 text-left font-medium text-gray-800"
    >
      {title}
      <SafeIcon icon={open ? FiChevronDown : FiChevronRight} className="w-4 h-4 text-gray-500" />
    </button>
    {open && <div className="px-4 pb-4">{children}</div>}
  </div>
);

const fmt = (v) =>
  Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Auto-filled dealer forms for a deal. Give it a deal model (see
 * config/dealPacketForms.js) and it renders packet generation buttons plus a
 * review-and-adjust editor for everything that lands on the paperwork.
 */
const DealFormsPanel = ({ model: initialModel, onModelChange }) => {
  const [model, setModel] = useState(initialModel);
  const [openSection, setOpenSection] = useState('');
  const [busy, setBusy] = useState('');
  const [status, setStatus] = useState(null); // { kind: 'ok'|'err', text }
  const [showAmortization, setShowAmortization] = useState(false);

  useEffect(() => {
    setModel(initialModel);
  }, [initialModel]);

  const update = (patch) => {
    setModel((prev) => {
      const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
      if (onModelChange) onModelChange(next);
      return next;
    });
  };
  const setBuyer = (k, v) => update((p) => ({ ...p, buyer: { ...p.buyer, [k]: v } }));
  const setVehicle = (k, v) => update((p) => ({ ...p, vehicle: { ...p.vehicle, [k]: v } }));
  const setTrade = (k, v) => update((p) => ({ ...p, trade: { ...p.trade, [k]: v } }));
  const setMoney = (k, v) => update((p) => ({ ...p, money: { ...p.money, [k]: v } }));
  const setOverride = (k, v) =>
    update((p) => {
      const overrides = { ...p.overrides };
      if (v === '' || v == null) delete overrides[k];
      else overrides[k] = v;
      return { ...p, overrides };
    });

  const derived = useMemo(() => computeDerived(model), [model]);
  const packets = packetsForDealType(model.dealType);
  const isFinancing = model.dealType === 'bhph' || model.dealType === 'financed';

  const generate = async (packetId, how) => {
    setBusy(`${packetId}:${how}`);
    setStatus(null);
    try {
      const action = how === 'open' ? openPacket : downloadPacket;
      const result = await action(packetId, model);
      if (result.missing.length > 0) {
        console.warn('Deal packet fields missing from template:', result.missing);
      }
      setStatus({ kind: 'ok', text: `${result.packet.label} generated (${result.filled.length} fields filled).` });
    } catch (err) {
      console.error('Deal packet generation failed:', err);
      setStatus({ kind: 'err', text: err.message || 'Failed to generate packet.' });
    } finally {
      setBusy('');
    }
  };

  const deferredDowns = model.money.deferredDowns || [];

  return (
    <div className="space-y-4">
      {/* Deal type */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-700 mr-1">Deal type:</span>
        {DEAL_TYPES.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => update({ dealType: t.value })}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              model.dealType === t.value
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
        {isFinancing && (
          <button
            type="button"
            onClick={() => setShowAmortization(true)}
            className="ml-auto px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"
            title="Payment-by-payment schedule with principal/interest split"
          >
            <SafeIcon icon={FiTable} className="w-4 h-4" />
            Amortization
          </button>
        )}
      </div>

      {showAmortization && (
        <AmortizationModal model={model} onClose={() => setShowAmortization(false)} />
      )}

      {status && (
        <div
          className={`text-sm rounded-lg px-4 py-2 ${
            status.kind === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
        >
          {status.text}
        </div>
      )}

      {/* Packet cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {packets.map((p) => (
          <div key={p.id} className={`border rounded-lg p-4 ${p.supplemental ? 'bg-gray-50' : 'bg-white'}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                  <SafeIcon icon={FiFileText} className="w-4 h-4 text-primary-600" />
                  {p.label}
                </div>
                <p className="text-xs text-gray-600 mt-1">{p.description}</p>
                <p className="text-xs text-gray-400 mt-0.5">{p.pages} pages · auto-filled from this deal</p>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                disabled={!!busy}
                onClick={() => generate(p.id, 'download')}
                className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                {busy === `${p.id}:download` ? 'Filling…' : 'Download'}
              </button>
              <button
                type="button"
                disabled={!!busy}
                onClick={() => generate(p.id, 'open')}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
                title="Open in a new tab"
              >
                <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                {busy === `${p.id}:open` ? '…' : 'Open'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Review & adjust */}
      <div className="space-y-2">
        <Section
          title="Buyer & sale info"
          open={openSection === 'buyer'}
          onToggle={() => setOpenSection(openSection === 'buyer' ? '' : 'buyer')}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <TextInput label="First name" value={model.buyer.firstName} onChange={(v) => setBuyer('firstName', v)} />
            <TextInput label="Last name" value={model.buyer.lastName} onChange={(v) => setBuyer('lastName', v)} />
            <TextInput label="Email" value={model.buyer.email} onChange={(v) => setBuyer('email', v)} />
            <TextInput label="Street address" value={model.buyer.address} onChange={(v) => setBuyer('address', v)} />
            <TextInput label="City" value={model.buyer.city} onChange={(v) => setBuyer('city', v)} />
            <TextInput label="State" value={model.buyer.state} onChange={(v) => setBuyer('state', v)} />
            <TextInput label="ZIP" value={model.buyer.zip} onChange={(v) => setBuyer('zip', v)} />
            <TextInput label="Sale date" type="date" value={model.saleDate} onChange={(v) => update({ saleDate: v })} />
            <TextInput label="Salesperson" value={model.salesperson} onChange={(v) => update({ salesperson: v })} />
            <TextInput label="Dealer rep" value={model.dealerRep} onChange={(v) => update({ dealerRep: v })} />
          </div>
        </Section>

        <Section
          title="Vehicle"
          open={openSection === 'vehicle'}
          onToggle={() => setOpenSection(openSection === 'vehicle' ? '' : 'vehicle')}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <TextInput label="VIN" value={model.vehicle.vin} onChange={(v) => setVehicle('vin', v)} />
            <TextInput label="Stock #" value={model.vehicle.stockNumber} onChange={(v) => setVehicle('stockNumber', v)} />
            <TextInput label="Year" value={model.vehicle.year} onChange={(v) => setVehicle('year', v)} />
            <TextInput label="Make" value={model.vehicle.make} onChange={(v) => setVehicle('make', v)} />
            <TextInput label="Model" value={model.vehicle.model} onChange={(v) => setVehicle('model', v)} />
            <TextInput label="Body code (PK, 4D, LL…)" value={model.vehicle.body} onChange={(v) => setVehicle('body', v)} />
            <TextInput label="Color" value={model.vehicle.color} onChange={(v) => setVehicle('color', v)} />
            <TextInput label="Odometer" value={model.vehicle.mileage} onChange={(v) => setVehicle('mileage', v)} />
            <Field label="Condition">
              <select value={model.vehicle.condition} onChange={(e) => setVehicle('condition', e.target.value)} className={inputCls}>
                <option value="USED">USED</option>
                <option value="NEW">NEW</option>
              </select>
            </Field>
          </div>
        </Section>

        <Section
          title="Trade-in"
          open={openSection === 'trade'}
          onToggle={() => setOpenSection(openSection === 'trade' ? '' : 'trade')}
        >
          <label className="flex items-center gap-2 text-sm mb-3">
            <input
              type="checkbox"
              checked={model.trade.hasTrade}
              onChange={(e) => setTrade('hasTrade', e.target.checked)}
            />
            Customer has a trade-in
          </label>
          {model.trade.hasTrade && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <TextInput label="Year" value={model.trade.year} onChange={(v) => setTrade('year', v)} />
              <TextInput label="Make" value={model.trade.make} onChange={(v) => setTrade('make', v)} />
              <TextInput label="Model" value={model.trade.model} onChange={(v) => setTrade('model', v)} />
              <MoneyInput label="Allowance" value={model.trade.allowance} onChange={(v) => setTrade('allowance', v)} />
              <MoneyInput label="Payoff" value={model.trade.payoff} onChange={(v) => setTrade('payoff', v)} />
            </div>
          )}
        </Section>

        <Section
          title="Price, tax & fees"
          open={openSection === 'money'}
          onToggle={() => setOpenSection(openSection === 'money' ? '' : 'money')}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MoneyInput label="Sale price" value={model.money.salePrice} onChange={(v) => setMoney('salePrice', v)} />
            <MoneyInput label="Accessories / products" value={model.money.accessoriesTotal} onChange={(v) => setMoney('accessoriesTotal', v)} />
            <MoneyInput label="Rebate" value={model.money.rebate} onChange={(v) => setMoney('rebate', v)} />
            <MoneyInput label="Tax rate %" value={model.money.taxRate} onChange={(v) => setMoney('taxRate', v)} />
            <MoneyInput label="Documentary fee" value={model.money.docFee} onChange={(v) => setMoney('docFee', v)} />
            <MoneyInput label="Title fee" value={model.money.titleFee} onChange={(v) => setMoney('titleFee', v)} />
            <MoneyInput label="License / registration" value={model.money.licenseRegFee} onChange={(v) => setMoney('licenseRegFee', v)} />
            <MoneyInput label="Inventory tax rate %" value={model.money.inventoryTaxRate} onChange={(v) => setMoney('inventoryTaxRate', v)} />
            {!(model.money.inventoryTaxRate > 0) && (
              <MoneyInput label="Inventory tax amount" value={model.money.inventoryTaxAmount} onChange={(v) => setMoney('inventoryTaxAmount', v)} />
            )}
            <MoneyInput label="Windshield sticker fee" value={model.money.windshieldFee} onChange={(v) => setMoney('windshieldFee', v)} />
            <MoneyInput label="County rd & bridge fee" value={model.money.countyRdBridgeFee} onChange={(v) => setMoney('countyRdBridgeFee', v)} />
            <MoneyInput label="Processing & handling" value={model.money.processingFee} onChange={(v) => setMoney('processingFee', v)} />
            <MoneyInput label="Reg fee DPS" value={model.money.regFeeDps} onChange={(v) => setMoney('regFeeDps', v)} />
            <MoneyInput label="Inspection fee" value={model.money.inspectionFee} onChange={(v) => setMoney('inspectionFee', v)} />
            <MoneyInput label="Insp. replacement program" value={model.money.inspectionReplacementFee} onChange={(v) => setMoney('inspectionReplacementFee', v)} />
            <MoneyInput label="Other fees" value={model.money.otherFees} onChange={(v) => setMoney('otherFees', v)} />
          </div>
        </Section>

        <Section
          title="Down payment & financing"
          open={openSection === 'financing'}
          onToggle={() => setOpenSection(openSection === 'financing' ? '' : 'financing')}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MoneyInput label="Cash down" value={model.money.cashDown} onChange={(v) => setMoney('cashDown', v)} />
            {isFinancing && (
              <>
                <MoneyInput label="APR %" value={model.money.apr} onChange={(v) => setMoney('apr', v)} />
                <MoneyInput label="Number of payments" value={model.money.numPayments} onChange={(v) => setMoney('numPayments', v)} />
                <Field label="Payment frequency">
                  <select
                    value={model.money.paymentFrequency}
                    onChange={(e) => setMoney('paymentFrequency', e.target.value)}
                    className={inputCls}
                  >
                    {PAYMENT_FREQUENCIES.map((f) => (
                      <option key={f.value} value={f.value}>{f.value}</option>
                    ))}
                  </select>
                </Field>
                <TextInput label="First payment date" type="date" value={model.money.firstPaymentDate} onChange={(v) => setMoney('firstPaymentDate', v)} />
                <MoneyInput label="Final payment (if different)" value={model.money.finalPaymentAmount} onChange={(v) => setMoney('finalPaymentAmount', v)} />
                <TextInput label="Final payment date" type="date" value={model.money.finalPaymentDate} onChange={(v) => setMoney('finalPaymentDate', v)} />
                {model.dealType === 'bhph' && (
                  <MoneyInput label="Max insurance deductible" value={model.money.maxDeductible} onChange={(v) => setMoney('maxDeductible', v)} />
                )}
              </>
            )}
          </div>

          {isFinancing && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Deferred down payments (pickup notes)</span>
                {deferredDowns.length < 4 && (
                  <button
                    type="button"
                    onClick={() => setMoney('deferredDowns', [...deferredDowns, { date: '', amount: 0 }])}
                    className="text-primary-600 text-sm flex items-center gap-1 hover:text-primary-700"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add
                  </button>
                )}
              </div>
              {deferredDowns.length === 0 && (
                <p className="text-xs text-gray-500">None — add one if part of the down payment comes later.</p>
              )}
              <div className="space-y-2">
                {deferredDowns.map((d, i) => (
                  <div key={i} className="flex items-end gap-2">
                    <div className="flex-1">
                      <TextInput
                        label={`Payment ${i + 1} date`}
                        type="date"
                        value={d.date}
                        onChange={(v) =>
                          setMoney('deferredDowns', deferredDowns.map((x, j) => (j === i ? { ...x, date: v } : x)))
                        }
                      />
                    </div>
                    <div className="flex-1">
                      <MoneyInput
                        label="Amount"
                        value={d.amount}
                        onChange={(v) =>
                          setMoney('deferredDowns', deferredDowns.map((x, j) => (j === i ? { ...x, amount: v } : x)))
                        }
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setMoney('deferredDowns', deferredDowns.filter((_, j) => j !== i))}
                      className="p-2 text-red-500 hover:text-red-700"
                      title="Remove"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        <Section
          title="Computed figures (override if needed)"
          open={openSection === 'derived'}
          onToggle={() => setOpenSection(openSection === 'derived' ? '' : 'derived')}
        >
          <p className="text-xs text-gray-500 mb-3">
            These are calculated from the deal and printed on the forms. Type in a box to override a figure; clear it
            to go back to the calculated value.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(DERIVED_LABELS).map(([key, label]) => (
              <Field key={key} label={label}>
                <input
                  type="number"
                  step="0.01"
                  value={model.overrides?.[key] ?? ''}
                  placeholder={fmt(derived[key])}
                  onChange={(e) => setOverride(key, e.target.value)}
                  className={`${inputCls} ${model.overrides?.[key] != null ? 'border-amber-400 bg-amber-50' : ''}`}
                />
              </Field>
            ))}
          </div>
        </Section>
      </div>

      <p className="text-xs text-gray-400">
        Generated PDFs keep live form fields, so anything can still be corrected in the PDF viewer before printing.
      </p>
    </div>
  );
};

export default DealFormsPanel;
